import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlus, BookOpen, PenLine, GraduationCap, ArrowRight, ChevronDown, ChevronUp, CheckCircle
} from 'lucide-react';
import { FAQS } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{question}</span>
        {open ? <ChevronUp size={17} className="text-blue-600 shrink-0" /> : <ChevronDown size={17} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 bg-white border-t border-gray-100">
          <p className="text-gray-600 text-sm leading-relaxed pt-3">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HowItWorks() {
  const { t } = useSiteLanguage();

  const STEPS = [
    { number: '01', icon: BookOpen, titleKey: 'hiw.step1.title', descKey: 'hiw.step1.desc', tipKey: 'hiw.step1.tip' },
    { number: '02', icon: UserPlus,  titleKey: 'hiw.step2.title', descKey: 'hiw.step2.desc', tipKey: 'hiw.step2.tip' },
    { number: '03', icon: PenLine,   titleKey: 'hiw.step3.title', descKey: 'hiw.step3.desc', tipKey: 'hiw.step3.tip' },
    { number: '04', icon: GraduationCap, titleKey: 'hiw.step4.title', descKey: 'hiw.step4.desc', tipKey: 'hiw.step4.tip' },
  ];

  const BENEFITS = [
    { titleKey: 'hiw.benefit1.title', descKey: 'hiw.benefit1.desc' },
    { titleKey: 'hiw.benefit2.title', descKey: 'hiw.benefit2.desc' },
    { titleKey: 'hiw.benefit3.title', descKey: 'hiw.benefit3.desc' },
    { titleKey: 'hiw.benefit4.title', descKey: 'hiw.benefit4.desc' },
    { titleKey: 'hiw.benefit5.title', descKey: 'hiw.benefit5.desc' },
    { titleKey: 'hiw.benefit6.title', descKey: 'hiw.benefit6.desc' },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="How It Works"
        description="From application to qualification — see how studying an accredited online course with Atlas Academy works, step by step."
        path="/how-it-works"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('hiw.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-5xl font-extrabold mb-4">{t('hiw.hero.title')}</h1>
          <p className="text-blue-100/70 max-w-xl text-lg leading-relaxed">
            {t('hiw.hero.desc')}
          </p>
        </div>
      </div>

      {/* Steps */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-10">
            {STEPS.map((step, i) => (
              <div
                key={step.number}
                className={`flex flex-col lg:flex-row gap-8 items-start ${
                  i % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-5xl font-extrabold text-blue-100">{step.number}</span>
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                      <step.icon size={22} className="text-white" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{t(step.titleKey)}</h2>
                  <p className="text-gray-600 leading-relaxed mb-4">{t(step.descKey)}</p>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
                    <CheckCircle size={15} className="text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-blue-800 text-sm">{t(step.tipKey)}</p>
                  </div>
                </div>
                <div className="w-full lg:w-80 shrink-0">
                  <div className="bg-gray-50 rounded-2xl h-52 flex items-center justify-center border border-gray-100">
                    <step.icon size={64} className="text-blue-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why online learning */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('hiw.benefits.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('hiw.benefits.title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map(item => (
              <div key={item.titleKey} className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow">
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{t(item.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('hiw.faqs.eyebrow')}</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('hiw.faqs.title')}</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map(faq => (
              <FAQItem
                key={faq.id}
                question={t(`faq.${faq.id}.q`, faq.question)}
                answer={t(`faq.${faq.id}.a`, faq.answer)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f2b4a] py-14">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">{t('hiw.cta.title')}</h2>
            <p className="text-blue-200/70">{t('hiw.cta.desc')}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/courses" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors">
              {t('hiw.cta.viewCourses')} <ArrowRight size={15} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors">
              {t('hiw.cta.contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
